/*
 * @format
 */

import React from 'react';
import { Circle, ClipPath, Defs, G, Path, Text } from 'react-native-svg';

import { FACE_RADIUS, RING_RADIUS } from './WatchFace';
import {
  ARC_START_DEG,
  ARC_SWEEP_DEG,
  Crown,
  crownBoxFor,
  dialGeometry,
  dp,
  LABEL_SIZE,
  RING_STROKE,
  sp,
  StepButton,
  STEP_BUTTON_RADIUS,
  stepButtonCentresFor,
  TurnArrows,
  VALUE_SIZE,
} from './WatchEditorParts';
import {
  DiagramFrame,
  HighlightBand,
  Label,
  Leader,
  MEDIUM,
  REGULAR,
  stopShortOf,
  TapDot,
  TAP_RADIUS,
} from './DiagramParts';
import { accent, black, primary } from '../../colours';

/*
 * The duration editor, ported from PlusHoursEditor.kt: a plain progress arc for
 * the chosen duration with a white handle at its end, a ± button above and
 * below, and the value in the middle — the value being the doorway that saves.
 *
 * Annotated with the two ways to change it: the buttons, and the crown or bezel.
 */
// The editor's own range: 0–6 hours in 5 minute steps.
const PLUS_MAX_MINUTES = 360;
const MINUTES = 140; // 2:20, the example the copy walks through

const WIDTH = 360;
const HEIGHT = 430;
const CX = WIDTH / 2;
const CY = 176;

const HANDLE_DOT = dp(6);
const HANDLE_OUTLINE = dp(2);

const HINT_SIZE = sp(12); // bodySmall

// The duration is the hero and sits on the face's centre — the same baseline
// offset the zone dial gives its setpoint — with the label lifted clear above
// it and the hint below.
const LABEL_DY = -dp(28);
const VALUE_DY = dp(14);
const HINT_DY = dp(38);

const TRACK_GREY = primary(900);
const DIM_GREY = primary(300);
const ACCENT_GREEN = accent(400);
const NEUTRAL_WHITE = primary(50);

const fillFraction = MINUTES / PLUS_MAX_MINUTES;

const { pointAt, arcPath } = dialGeometry(CX, CY);

const handle = pointAt(RING_RADIUS, ARC_START_DEG + ARC_SWEEP_DEG * fillFraction);

const { plus: plusCentre, minus: minusCentre } = stepButtonCentresFor(CX, CY);
/*
 * The controls caption's two leaders. Each aims at an edge rather than a middle
 * — the dot would otherwise sit on the − itself — and then stops short of it, so
 * the line points at the control instead of touching it.
 */
const CONTROLS_LEADER_FROM = { x: 250, y: 372 };
const LEADER_CLEARANCE = 14;

const MINUS_ANCHOR = stopShortOf(
  CONTROLS_LEADER_FROM,
  { x: minusCentre.x, y: minusCentre.y + STEP_BUTTON_RADIUS },
  LEADER_CLEARANCE,
);

/*
 * The band covers the value — the tap target that saves — and is centred on it.
 * A text's y is its baseline, with the digits standing above it, so the middle
 * of the numeral is half a cap-height up from there rather than at its y.
 */
const VALUE_CAP_HEIGHT = VALUE_SIZE * 0.7;
const VALUE_MIDDLE = CY + VALUE_DY - VALUE_CAP_HEIGHT / 2;
const BAND_PADDING = dp(10);
const BAND_HEIGHT = VALUE_CAP_HEIGHT + BAND_PADDING * 2;
const BAND = {
  x: CX - FACE_RADIUS,
  y: VALUE_MIDDLE - BAND_HEIGHT / 2,
  width: FACE_RADIUS * 2,
  height: BAND_HEIGHT,
};

// On the band's own centre line, clear of the numeral so it stays readable.
const SAVE_TAP = { x: CX - dp(70), y: VALUE_MIDDLE };

/*
 * The save caption's leader, drawn like the controls caption's pair. It stops
 * short of the fingertip rather than touching it, but by less than the controls'
 * clearance: a full 14 would leave the dot sitting on the progress arc, where it
 * would read as naming the arc instead of the tap. This lands it inside the arc,
 * on the band the fingertip is pressing.
 */
const SAVE_LEADER_FROM = { x: 40, y: 78 };
const SAVE_LEADER_CLEARANCE = 4;
const SAVE_ANCHOR = stopShortOf(
  SAVE_LEADER_FROM,
  SAVE_TAP,
  TAP_RADIUS + SAVE_LEADER_CLEARANCE,
);

const CROWN = crownBoxFor(CX, CY);
/*
 * The crown's OUTER bottom corner. The inner one sits inside the circle the turn
 * arrows run on, so a leader reaching it has to cut across the lower arrow to get
 * there; the outer corner is beyond that circle, so the whole line stays clear of
 * both arrows.
 */
const CROWN_ANCHOR = stopShortOf(
  CONTROLS_LEADER_FROM,
  { x: CROWN.x + CROWN.width, y: CROWN.y + CROWN.height },
  LEADER_CLEARANCE,
);

function WatchPlusHoursDiagram() {
  return (
    <DiagramFrame
      width={WIDTH}
      height={HEIGHT}
      description={
        'The watch Plus Hours editor, showing a duration of 2 hours 20 ' +
        'minutes with a plus button above it and a minus button below, and ' +
        'the crown alongside. A line points to a fingertip on the duration, ' +
        'which is what saves it.'
      }>
      <Defs>
        <ClipPath id="plus-hours-face">
          <Circle cx={CX} cy={CY} r={FACE_RADIUS} />
        </ClipPath>
      </Defs>

      <Circle cx={CX} cy={CY} r={FACE_RADIUS} fill={black} />

      {/* A plain progress arc: the fill is the chosen duration and the white
          dot its handle — no target marker, no trail. */}
      <Path
        d={arcPath(RING_RADIUS, ARC_START_DEG, ARC_START_DEG + ARC_SWEEP_DEG)}
        stroke={TRACK_GREY}
        strokeWidth={RING_STROKE}
        strokeLinecap="round"
        fill="none"
      />
      <Path
        d={arcPath(
          RING_RADIUS,
          ARC_START_DEG,
          ARC_START_DEG + ARC_SWEEP_DEG * fillFraction,
        )}
        stroke={ACCENT_GREEN}
        strokeWidth={RING_STROKE}
        strokeLinecap="round"
        fill="none"
      />
      <Circle
        cx={handle.x}
        cy={handle.y}
        r={HANDLE_DOT}
        fill={NEUTRAL_WHITE}
        stroke={black}
        strokeWidth={HANDLE_OUTLINE}
      />

      <StepButton centre={plusCentre} glyph="+" />
      <StepButton centre={minusCentre} glyph="−" />

      <Text
        x={CX}
        y={CY + LABEL_DY}
        fill={DIM_GREY}
        fontFamily={MEDIUM}
        fontSize={LABEL_SIZE}
        textAnchor="middle">
        Plus Hours
      </Text>
      <Text
        x={CX}
        y={CY + VALUE_DY}
        fill={ACCENT_GREEN}
        fontFamily={MEDIUM}
        fontSize={VALUE_SIZE}
        textAnchor="middle">
        2:20
      </Text>
      <Text
        x={CX}
        y={CY + HINT_DY}
        fill={DIM_GREY}
        fontFamily={REGULAR}
        fontSize={HINT_SIZE}
        textAnchor="middle">
        Tap to set
      </Text>

      <G clipPath="url(#plus-hours-face)">
        <HighlightBand {...BAND} />
      </G>
      <TapDot x={SAVE_TAP.x} y={SAVE_TAP.y} />

      <TurnArrows cx={CX} cy={CY} />
      <Crown cx={CX} cy={CY} />

      <Leader from={SAVE_LEADER_FROM} to={SAVE_ANCHOR} />
      <Label x={6} y={40} bold heading={['Tap here to', 'save']} />

      <Leader from={CONTROLS_LEADER_FROM} to={MINUS_ANCHOR} />
      <Leader from={CONTROLS_LEADER_FROM} to={CROWN_ANCHOR} />
      <Label
        x={WIDTH - 6}
        y={396}
        anchor="end"
        bold
        heading={['+/- to change or rotate', 'crown / bezel']}
      />
    </DiagramFrame>
  );
}

export default WatchPlusHoursDiagram;
